import { Alert, Badge, Button } from '@mantine/core';
import { IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import { TablePager } from '../purchasing/table.js';
import { type AuditTrailData, auditValueText, offsetLabel } from './audit-trail.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { RowsSkeleton } from './PmsWorkParts.js';

export type AuditTrailState =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: AuditTrailData };

export interface AuditTrailViewProps {
  state: AuditTrailState;
  page: number;
  pageSize: number;
  /** History sayfasında kaydın türü de gösterilir; ekipman detayında gerekmez. */
  showRecord?: boolean;
  onPage: (page: number) => void;
  onRetry: () => void;
}

/**
 * Denetim kaydı (plan M7 Task 30; ADR 0029 K37) [NR496 4.6.3]: değişen alan (etiketi dilden),
 * eski ve yeni değer, kişi, bilgisayar, UTC ve gemi saati. Gemide yalnız gemide yapılan
 * değişiklikler vardır ve panel bunu yazar; ofiste hazırlanan kaydın olayı satırdan gelir.
 */
export function AuditTrailView({
  state,
  page,
  pageSize,
  showRecord = false,
  onPage,
  onRetry,
}: AuditTrailViewProps) {
  const { t } = useTranslation();
  const tableBox = useFitColumns<HTMLElement>();
  const errorText = usePmsErrorText();
  const k = (key: string, values: Record<string, unknown> = {}) => t(`pmsAudit.${key}`, values);
  const label = (prefix: string, key: string) => {
    const value = t(`pmsAudit.${prefix}.${key}`);
    return value === `pmsAudit.${prefix}.${key}` ? key : value;
  };
  const data = state.kind === 'ready' ? state.data : null;
  let body: ReactNode;
  if (state.kind === 'loading') body = <RowsSkeleton />;
  else if (state.kind === 'error') {
    body = (
      <EmptyState
        tone="danger"
        title={k('error')}
        description={errorText({ code: state.code })}
        action={
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} aria-hidden />}
            onClick={onRetry}
          >
            {k('retry')}
          </Button>
        }
      />
    );
  } else if (state.data.events.length === 0) body = <EmptyState title={k('empty')} />;
  else {
    const from = (page - 1) * pageSize + 1;
    const to = from + state.data.events.length - 1;
    body = (
      <>
        {/* Dar kutuda önce cihaz, sonra UTC saati (gemi saati farkıyla duruyor), sonra kayıt türü
            gizlenir (`fit-columns.ts`) */}
        <section
          ref={tableBox}
          className={classes.tableBox}
          aria-label={k('table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={classes.table} data-cards>
            <thead>
              <tr>
                <th scope="col" data-drop={2}>
                  {k('columns.at')}
                </th>
                <th scope="col">{k('columns.shipTime')}</th>
                {showRecord ? (
                  <th scope="col" data-drop={3}>
                    {k('columns.record')}
                  </th>
                ) : null}
                <th scope="col">{k('columns.op')}</th>
                <th scope="col">{k('columns.field')}</th>
                <th scope="col">{k('columns.oldValue')}</th>
                <th scope="col">{k('columns.newValue')}</th>
                <th scope="col">{k('columns.user')}</th>
                <th scope="col" data-drop={1}>
                  {k('columns.device')}
                </th>
              </tr>
            </thead>
            <tbody>
              {state.data.events.map((event) => (
                <tr key={event.id} data-derived={event.source === 'row' || undefined}>
                  <td data-label={k('columns.at')} data-drop={2}>
                    {formatDateTime(event.at, 0)}
                  </td>
                  <td data-label={k('columns.shipTime')}>
                    {event.tzOffsetMin === null
                      ? (event.shipTime ?? '—')
                      : `${formatDateTime(event.at, event.tzOffsetMin)} (${offsetLabel(event.tzOffsetMin)})`}
                  </td>
                  {showRecord ? (
                    <td data-label={k('columns.record')} data-drop={3}>
                      {label('tables', event.table)}
                    </td>
                  ) : null}
                  <td data-label={k('columns.op')}>{label('ops', event.op)}</td>
                  <td data-label={k('columns.field')}>
                    {event.field ? label('fields', event.field) : '—'}
                  </td>
                  <td data-label={k('columns.oldValue')} className={work.wrapCell}>
                    {auditValueText(event.oldValue)}
                  </td>
                  <td data-label={k('columns.newValue')} className={work.wrapCell}>
                    {auditValueText(event.newValue)}
                  </td>
                  <td data-label={k('columns.user')}>{event.user.name ?? '—'}</td>
                  <td data-label={k('columns.device')} data-drop={1}>
                    {event.device.name ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <TablePager
          page={page}
          pages={Math.max(1, Math.ceil(state.data.total / pageSize))}
          summary={k('showing', { from, to, total: state.data.total })}
          label={k('pagination')}
          onPage={onPage}
        />
      </>
    );
  }
  return (
    <Panel
      title={k('title')}
      extra={
        data?.officeHistory ? (
          <Badge variant="light" color="gray">
            {k('officeHistory')}
          </Badge>
        ) : undefined
      }
    >
      <div aria-busy={state.kind === 'loading'}>
        {data?.scope === 'ship' ? (
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" mb="sm">
            {k('shipNote')}
          </Alert>
        ) : null}
        {body}
      </div>
    </Panel>
  );
}

export interface AuditTrailPanelProps {
  /** Sayfanın verisi; `limit` ve `offset` panelden. */
  load: (page: { limit: number; offset: number }) => Promise<AuditTrailData>;
  showRecord?: boolean;
  pageSize?: number;
}

/**
 * Ortak denetim kaydı paneli: ekipman detayının History sekmesinde (tek kayıt) ve History
 * sayfasında (bütün PMS kayıtları; `showRecord`). Yükleniyor, hata ve boş durumları; sayfalı.
 */
export function AuditTrailPanel({ load, showRecord, pageSize = 12 }: AuditTrailPanelProps) {
  const [page, setPage] = useState(1);
  const [state, setState] = useState<AuditTrailState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  useEffect(() => {
    let live = true;
    setState({ kind: 'loading' });
    // Yeniden dene sayacı aynı sayfayı yeniden okur
    void attempt;
    load({ limit: pageSize, offset: (page - 1) * pageSize })
      .then((data) => {
        if (live) setState({ kind: 'ready', data });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [load, page, pageSize, attempt]);
  return (
    <AuditTrailView
      state={state}
      page={page}
      pageSize={pageSize}
      {...(showRecord === undefined ? {} : { showRecord })}
      onPage={setPage}
      onRetry={retry}
    />
  );
}
